import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Timer, FileCheck, Recycle } from "lucide-react";
import PickupFormCard from "@/components/landing/PickupFormCard";

const EASE = [0.16, 1, 0.3, 1];

const BULLETS = [
  { icon: Timer, text: "Logistics team responds within 24 hours" },
  { icon: Recycle, text: "Free pickup for 5+ high-value items, Australia-wide" },
  { icon: ShieldCheck, text: "100% data destruction on every data-bearing device" },
  { icon: FileCheck, text: "ESG report & Certificate of Destruction included" },
];

export default function BookPickup() {
  return (
    <div className="noise-overlay min-h-screen bg-navy" data-testid="book-pickup-page">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" data-testid="book-pickup-logo" className="flex items-center gap-3">
            <img src="/images/revanta-mark.png" alt="Revanta Asset Management" className="h-9 w-auto" />
            <span className="font-display text-sm font-bold uppercase tracking-[0.25em] text-white">
              Revanta
            </span>
          </Link>
          <a
            href="tel:+61466426343"
            data-testid="book-pickup-phone"
            className="inline-flex items-center gap-2 rounded-full border border-eco/40 px-4 py-2 text-xs font-bold text-eco transition-[background-color,color] duration-300 hover:bg-eco hover:text-navy sm:text-sm"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">Questions? Call</span> +61 466 426 343
          </a>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-14 lg:grid-cols-12 lg:gap-14 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-6"
        >
          <p
            className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-eco"
            data-testid="book-pickup-overline"
          >
            Free Corporate Pickup — Australia-Wide
          </p>
          <h1
            className="font-display text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl"
            data-testid="book-pickup-heading"
          >
            Book your e-waste collection in
            <span className="text-eco"> 60 seconds.</span>
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-300" data-testid="book-pickup-subtext">
            Tell us what you have and we handle the rest — secure removal from your premises,
            certified data destruction, and a full ESG recycling report for your records.
          </p>
          <ul className="mt-10 flex flex-col gap-5" data-testid="book-pickup-benefits">
            {BULLETS.map(({ icon: Icon, text }, i) => (
              <motion.li
                key={text}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.15 + i * 0.1 }}
                className="flex items-center gap-4"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy-800 ring-1 ring-eco/30">
                  <Icon className="h-5 w-5 text-eco" strokeWidth={2} />
                </span>
                <span className="text-sm font-medium text-slate-200 sm:text-base">{text}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          className="lg:col-span-6"
        >
          <PickupFormCard />
        </motion.div>
      </main>

      <footer className="border-t border-white/10 py-6">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 px-6 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500" data-testid="book-pickup-copyright">
            © {new Date().getFullYear()} Revanta Asset Management — Australia
          </p>
          <div className="flex gap-5">
            <Link to="/privacy" data-testid="book-pickup-privacy-link" className="text-xs text-slate-500 transition-colors hover:text-eco">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="book-pickup-terms-link" className="text-xs text-slate-500 transition-colors hover:text-eco">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
